import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { fetchAplicacaoAoAcervo } from '@/api/regrasDistribuicao';
import { invalidarDistribuicao } from '@/components/escritorio/ResponsaveisDialog';
import { queryKeys } from '@/lib/pwa/queryConfig';

/**
 * Acompanha a aplicação das regras ao acervo (docs/PLANO_REGRAS_DISTRIBUICAO.md, Bloco 3) de qualquer
 * aba do escritório: enquanto ela está em andamento, atualiza a cada 5 s, e a fila "Para distribuir"
 * e as "Aplicadas" acompanham cada passada — mesmo com o gestor na aba Distribuir, onde ele poderia
 * distribuir à mão o que as regras acabaram de levar. Quando sai de andamento, tudo o que mostra
 * quem cuida é recarregado (lista, painel, agenda).
 */
export function useAcompanharLotes(escritorioId: string, habilitado: boolean) {
  const queryClient = useQueryClient();
  const aplicacao = useQuery({
    queryKey: queryKeys.escritorios.acervo(escritorioId),
    queryFn: () => fetchAplicacaoAoAcervo(escritorioId),
    enabled: !!escritorioId && habilitado,
    refetchInterval: (q) => (q.state.data?.estado === 'PENDENTE' ? 5_000 : false),
  });

  // Pelo id, não só pelo estado: uma aplicação que termina enquanto outra começa também conta.
  const pendente = aplicacao.data?.estado === 'PENDENTE' ? aplicacao.data.id : '';
  const antes = useRef(pendente);
  useEffect(() => {
    if (antes.current && antes.current !== pendente) invalidarDistribuicao(queryClient, escritorioId);
    antes.current = pendente;
  }, [pendente, queryClient, escritorioId]);

  // A cada passada que distribui (2.000 processos por vez), a fila deixa de ter o que já foi levado.
  const andou = pendente ? `${pendente}:${aplicacao.data?.distribuidos ?? 0}` : '';
  const andouAntes = useRef(andou);
  useEffect(() => {
    if (andou && andouAntes.current && andou !== andouAntes.current) {
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.fila(escritorioId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.aplicadas(escritorioId) });
    }
    andouAntes.current = andou;
  }, [andou, queryClient, escritorioId]);

  return aplicacao;
}
